import React from 'react';

import { useLocation } from 'react-router';

import get from 'lodash.get';
import qs from 'query-string';

// eslint-disable-next-line import/no-internal-modules
import { withLoginState } from '@xingternal/hops';
import { LOGIN_STATE } from '@xingternal/xing-frame-provider';
import * as Types from '@xingternal/pages-graphql-types';

import {
  redirectToLogin,
  useLoggedoutAction,
  getImageGender,
  capitalizeString,
} from '@xingternal/pages-common';
import { getTrackingUrl } from '@xingternal/pages-tracking';

import ProfileInfo from '@xingternal/profile-info';
import { IconMessages } from '@xingternal/icons';

import { XingIdContactsFragment } from '../../XingIdContactsFragment.gql-types';

import styles from './Contacts.css';

type ContactsProps = {
  currentUser?: string;
  messageEntryPoint: string;
  trackingModuleType?: string;
  loginState: string;
  users: (Pick<Types.EntityPageContactUser, 'id' | 'enabled' | 'label'> &
    XingIdContactsFragment)[];
  upsellActive?: boolean;
};

const Contacts = ({
  users,
  currentUser,
  messageEntryPoint,
  trackingModuleType,
  loginState,
  upsellActive,
}: ContactsProps) => {
  const location = useLocation();
  const sendMessage = (pageName: string, messageEntryPoint: string) => {
    if (loginState === LOGIN_STATE.loggedOut) {
      redirectToLogin('sendMessage', { recipientName: pageName });
    } else {
      window.location.assign(
        `/chats/new/${pageName}?contextId=entity_pages&entryPoint=${messageEntryPoint}`
      );
    }
  };

  useLoggedoutAction(
    () =>
      window.location.assign(
        `/chats/new/${get(
          qs.parse(location.search),
          'recipientName'
        )}?contextId=entity_pages&entryPoint=${messageEntryPoint}`
      ),
    'sendMessage'
  );

  return (
    <div className={styles.contactsWrapper} data-testid={'CONTACTS_MODULE'}>
      {users.map((user, index: number) => {
        const flag =
          user.userFlags?.displayFlag &&
          user.userFlags?.displayFlag !== 'BASIC' &&
          user.userFlags?.displayFlag !== 'EXECUTIVE'
            ? {
                size: 'small',
                variant: user.userFlags?.displayFlag?.toLowerCase(),
                children: capitalizeString(user.userFlags.displayFlag),
              }
            : null;
        return (
          <div
            key={index}
            className={upsellActive ? styles.contactUpsell : styles.contact}
            data-cy={'contact'}
            data-testid={`CONTACT_${user.pageName}`}
          >
            <ProfileInfo
              profileImage={{
                size: 'medium',
                src: user.profileImage?.[0] ? user.profileImage[0].url : null,
                type: getImageGender(user.gender),
                profileName: 'user profile image',
              }}
              profileUrl={getTrackingUrl({
                to:
                  user?.clickReasonProfileUrl?.profileUrl ||
                  `/profile/${user.pageName}`,
                trackingKey: 'viewContactProfile' + trackingModuleType,
              })}
              headline={{
                size: 'small',
                sizeWide: 'medium',
                children: user.displayName,
                as: 'strong',
              }}
              textBody={[
                {
                  size: 'small',
                  sizeWide: 'medium',
                  children: user.occupations && user.occupations[0]?.subline,
                },
              ]}
              flag={flag}
              textMeta={[
                {
                  size: 'small',
                  sizeWide: 'medium',
                  children: user.label,
                },
              ]}
              buttons={
                currentUser !== user.id
                  ? [
                      {
                        variant: 'tertiary',
                        icon: IconMessages,
                        'data-cy': 'message-btn',
                        className: styles.button,
                        size: 'medium',
                        'aria-label': 'message',
                        onClick: () =>
                          sendMessage(user.pageName, messageEntryPoint),
                      },
                    ]
                  : null
              }
              className={styles.profileInfo}
            />
          </div>
        );
      })}
    </div>
  );
};

export default withLoginState(Contacts);
